import { useState } from "react";

import { StepFooter } from "@/features/onboarding/ui/IdentityStep";
import { useUpdateWorkerProfile } from "@/features/workers/model/useWorkerProfile";
import {
  EXPERIENCE_LEVEL_DESCRIPTIONS,
  EXPERIENCE_LEVEL_LABELS,
  type ExperienceLevel,
  type WorkerProfile,
} from "@/features/workers/types";
import { cn } from "@/shared/lib/cn";

const LEVELS: ExperienceLevel[] = ["ENTRY", "INTERMEDIATE", "EXPERIENCED", "EXPERT"];

interface Props {
  profile: WorkerProfile;
  onSaved: () => void;
  onBack: () => void;
}

export function ExperienceStep({ profile, onSaved, onBack }: Props) {
  const [level, setLevel] = useState<ExperienceLevel | null>(profile.experience_level);
  const [bio, setBio] = useState(profile.bio ?? "");
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateWorkerProfile();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!level) {
      setError("Pick the level that fits you best.");
      return;
    }
    setError(null);
    await update.mutateAsync({
      experience_level: level,
      bio: bio.trim() || undefined,
    });
    onSaved();
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          Your experience
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          Helps venues match you to the right shifts.
        </p>
      </div>

      <div className="space-y-2">
        {LEVELS.map((lvl) => (
          <label
            key={lvl}
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-md border bg-surface p-3 transition-colors",
              level === lvl
                ? "border-primary ring-2 ring-primary ring-offset-1"
                : "border-border hover:bg-surface-sunken",
            )}
          >
            <input
              type="radio"
              name="experience-level"
              value={lvl}
              checked={level === lvl}
              onChange={() => setLevel(lvl)}
              className="mt-1 h-4 w-4 shrink-0"
            />
            <span className="flex flex-col">
              <span className="text-sm font-medium text-copy">
                {EXPERIENCE_LEVEL_LABELS[lvl]}
              </span>
              <span className="text-xs text-copy-muted">
                {EXPERIENCE_LEVEL_DESCRIPTIONS[lvl]}
              </span>
            </span>
          </label>
        ))}
      </div>

      <div className="space-y-1">
        <label htmlFor="bio" className="text-sm font-medium text-copy">
          A short bio <span className="text-copy-muted">(optional)</span>
        </label>
        <textarea
          id="bio"
          rows={3}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={1000}
          placeholder="3 years of restaurant work, Smart Serve certified, comfortable with high-volume service…"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <p className="text-xs text-copy-muted">{bio.length}/1000 characters</p>
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <StepFooter
        onBack={onBack}
        isSubmitting={update.isPending}
        nextDisabled={!level}
      />
    </form>
  );
}
